import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/**
 * What moment the map renders at. Cross-cutting UI state, so it lives in
 * `shared` beside `uiSession`/`liveSync` and every layer imports it downward —
 * a domain cannot import `app`.
 *
 * `followNow` is a FLAG, never a timestamp. Were Focus only a stored epoch it
 * would be one second in the past one second after load, and any "Focus is
 * behind Current Time" affordance would light up immediately and permanently.
 * Read Focus through `selectFocusMs(state, nowMs)`, never `state.focusMs`.
 *
 * `focusMs` is the COMMITTED value: written on seek, on pause, and at 4 Hz
 * while playing. The per-frame value never enters the store — a per-frame
 * dispatch here re-renders `DeckOverlay` and puts every slice's layer memo back
 * on the camera path.
 */
export interface MapFocusState {
  followNow: boolean;
  /** Absolute epoch ms. Meaningful only while `followNow` is false. */
  focusMs: number;
  isPlaying: boolean;
  /** Playing, but Focus is held at the loaded edge until history arrives. */
  isBuffering: boolean;
  /** Playback multiplier over wall-clock time. */
  speed: number;
  /**
   * Bumped by every jump of Focus: a seek, and a return to Current Time. A
   * reader compares it with the value it last saw, so the number itself means
   * nothing.
   */
  seekCount: number;
}

const initialState: MapFocusState = {
  followNow: true,
  focusMs: 0,
  isPlaying: false,
  isBuffering: false,
  speed: 1,
  seekCount: 0,
};

export const PLAYBACK_SPEEDS = [1, 60, 120] as const;

export const coercePlaybackSpeed = (value: number): number =>
  PLAYBACK_SPEEDS.find(speed => speed === value) ?? PLAYBACK_SPEEDS[0];

export const mapFocusSlice = createSlice({
  name: 'map/focus',
  initialState,
  reducers: {
    focusSeeked: (state, { payload }: PayloadAction<number>) => {
      state.isBuffering = false;
      state.followNow = false;
      state.focusMs = payload;
      state.seekCount += 1;
    },
    focusCommitted: (state, { payload }: PayloadAction<number>) => {
      state.focusMs = payload;
    },
    // Takes the instant play was pressed, because a follow-now Focus has no
    // stored epoch for the loop to advance from.
    playbackStarted: (state, { payload }: PayloadAction<number>) => {
      state.isBuffering = false;
      state.followNow = false;
      state.focusMs = payload;
      state.isPlaying = true;
    },
    playbackPaused: (state, { payload }: PayloadAction<number>) => {
      state.isBuffering = false;
      state.isPlaying = false;
      state.focusMs = payload;
    },
    // Playback reached Current Time. Restoring `followNow` is what keeps Focus
    // from drifting a second per second into the past from here.
    caughtUpToNow: state => {
      state.isBuffering = false;
      state.isPlaying = false;
      state.followNow = true;
    },
    followNowRestored: state => {
      state.isBuffering = false;
      state.isPlaying = false;
      state.followNow = true;
      state.seekCount += 1;
    },
    bufferingStarted: state => {
      state.isBuffering = true;
    },
    bufferingEnded: state => {
      state.isBuffering = false;
    },
    speedChanged: (state, { payload }: PayloadAction<number>) => {
      state.speed = coercePlaybackSpeed(payload);
    },
  },
});

export const mapFocusActions = mapFocusSlice.actions;
